import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Receipt, XCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const POLL_MS = 2000;
const MAX_POLLS = 20;

export default function DonorPaymentReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const cancelled = params.get("cancelled");
  const [state, setState] = useState(cancelled ? { phase: "cancelled" } : { phase: "checking" });
  const attempts = useRef(0);

  const poll = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await api.get(`/payments/status/${encodeURIComponent(sessionId)}`);
      if (res.data.payment_status === "paid") {
        setState({ phase: "paid", ...res.data });
        return;
      }
      if (["failed", "expired"].includes(res.data.payment_status)) {
        setState({ phase: "failed", ...res.data });
        return;
      }
    } catch {
      setState({ phase: "failed" });
      return;
    }
    attempts.current += 1;
    if (attempts.current >= MAX_POLLS) { setState({ phase: "timeout" }); return; }
    setTimeout(poll, POLL_MS);
  }, [sessionId]);

  useEffect(() => { if (!cancelled) poll(); }, [poll, cancelled]);

  const back = () => navigate("/donor", { replace: true });

  return (
    <div className="grid min-h-screen place-items-center bg-background px-4">
      <div className="panel w-full max-w-md p-8 text-center" data-testid="payment-return">
        {state.phase === "checking" && (
          <>
            <Loader2 className="mx-auto h-7 w-7 animate-spin text-primary" />
            <h1 className="mt-4 text-base font-semibold text-foreground md:text-lg">
              Confirming your gift with the payment provider
            </h1>
            <p className="mt-2 text-[12.5px] text-muted-foreground">
              This takes a few seconds. Please do not close the window.
            </p>
          </>
        )}
        {state.phase === "paid" && (
          <div data-testid="payment-return-paid">
            <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-500" />
            <h1 className="mt-4 text-base font-semibold text-foreground md:text-lg">
              Thank you — your gift of {state.currency} {state.amount?.toLocaleString()} is in
            </h1>
            <p className="mt-2 text-[12.5px] text-muted-foreground">
              Credited to {state.fund_code}
              {state.donation_reference ? ` as ${state.donation_reference}` : ""}.
              {state.receipt_no
                ? ` Your receipt ${state.receipt_no} is on your giving history.`
                : " Finance will confirm it and your receipt will follow."}
            </p>
            {state.receipt_no && (
              <div className="mt-3 flex items-center justify-center gap-1.5 text-[11.5px] text-primary">
                <Receipt className="h-3.5 w-3.5" /> {state.receipt_no}
              </div>
            )}
          </div>
        )}
        {state.phase === "cancelled" && (
          <div data-testid="payment-return-cancelled">
            <XCircle className="mx-auto h-8 w-8 text-muted-foreground" />
            <h1 className="mt-4 text-base font-semibold text-foreground md:text-lg">
              Payment cancelled
            </h1>
            <p className="mt-2 text-[12.5px] text-muted-foreground">
              Nothing has been charged. You can try again whenever you are ready.
            </p>
          </div>
        )}
        {(state.phase === "failed" || state.phase === "timeout") && (
          <div data-testid="payment-return-failed">
            <XCircle className="mx-auto h-8 w-8 text-red-500" />
            <h1 className="mt-4 text-base font-semibold text-foreground md:text-lg">
              We could not confirm this payment
            </h1>
            <p className="mt-2 text-[12.5px] text-muted-foreground">
              If your card was charged, the gift will appear on your giving history once the
              provider confirms it. Nothing is lost.
            </p>
          </div>
        )}
        <Button onClick={back} data-testid="payment-return-home"
                className="mt-6 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
          Back to my account
        </Button>
      </div>
    </div>
  );
}
